/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useLayoutEffect } from 'react';
import { Provider } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ConfigProvider, notification } from 'antd';
import { StyleSheetManager } from 'styled-components';

import PrivateRoute from '../containers/PrivateRoute';
import Main from '../containers/Main';
import { LOCATION } from '../constants/location';
import Blank from '../containers/Blank';
import Error from './Error';
import Unknown from './Unknown';
import { checkAsyncActionPermissionsWithLaunch } from '../constants/authorization';
import { CANCEL_REQUEST_MESSAGE } from '../constants/common';
import useCopyChartTooltipData from '../utils/hooks/useCopyChartTooltipData';
import { getTenantFromPath } from '../utils/common';
import ReauthModal from './ReauthModal';
import { requestReauth, subscribeReauth } from '../utils/reauth';
import { appNotification, setNotificationApi } from '../utils/notificationApi';

// Мост нотификаций: рендерит contextHolder (holder внутри дерева) и отдаёт его api в
// модульный ref. Так нотификации из любого места идут через этот holder, а не через
// статический detached-root, который роняет модалку реаутентификации.
const NotificationBridge = () => {
  const [api, contextHolder] = notification.useNotification();
  useEffect(() => setNotificationApi(api), [api]);
  return contextHolder;
};

// За сколько до истечения сессии показываем модалку реаутентификации.
const REAUTH_BUFFER_MS = 30_000;

const Root = (props) => {
  const {
    tenantId,
    applicationError,
    resetErrorMessage,
    store,
    tenants,
    permissions,
    setTenantId,
    isAuthenticated,
    fetchConfig,
    fetchMe,
    sessionExpiresAt,
    updateLocation,
    updatePreviousLocation,
    location,
  } = props;

  useCopyChartTooltipData();

  const { pathname } = useLocation();

  useLayoutEffect(() => {
    updatePreviousLocation(location);
    updateLocation(pathname);
  }, [pathname]);

  const fetchData = async () => {
    const {
      resetApp,
      fetchSegments,
      fetchOptions,
      fetchMetainf,
      fetchMapping,
      fetchLabels,
      fetchFunnels,
      fetchDashboards,
      fetchTemplates,
      setApplicationReady,
      checkActualization,
    } = props;

    resetApp();

    if (tenantId && isAuthenticated) {
      // permissions уже пришли в /me (карта по tenant'ам), берём готовый Set текущего tenant'а.
      // We have to fetch metainf before segments
      await checkAsyncActionPermissionsWithLaunch(fetchMetainf, permissions);

      await Promise.all(
        [
          fetchSegments,
          fetchOptions,
          fetchMapping,
          fetchLabels,
          fetchFunnels,
          fetchTemplates,
          fetchDashboards,
        ].map((item) => checkAsyncActionPermissionsWithLaunch(item, permissions))
      );
      checkActualization();
    }
    setApplicationReady();
  };

  useEffect(() => {
    if (tenantId) {
      fetchData();
    }
  }, [tenantId]);

  useEffect(() => {
    // bootstrap: сначала config (backendUrl), затем probe сессии /me.
    // Успех -> authenticated + tenants + permissionsByTenant + sessionExpiresAt.
    const bootstrap = async () => {
      await fetchConfig();
      await fetchMe();
    };
    bootstrap();
  }, []);

  // Проактивная реаутентификация: если до истечения сессии осталось меньше буфера — показываем
  // модалку сразу (в т.ч. после перезагрузки страницы), иначе планируем показ к этому моменту.
  useEffect(() => {
    if (!sessionExpiresAt) {
      return undefined;
    }
    const untilExpiry = sessionExpiresAt - Date.now();
    if (untilExpiry <= REAUTH_BUFFER_MS) {
      requestReauth();
      return undefined;
    }
    const timer = window.setTimeout(requestReauth, untilExpiry - REAUTH_BUFFER_MS);
    return () => window.clearTimeout(timer);
  }, [sessionExpiresAt]);

  // После любой успешной реаутентификации перечитываем /me: свежий sessionExpiresAt
  // перевзводит проактивный таймер (в т.ч. для пути 401 и соседних вкладок).
  useEffect(
    () =>
      subscribeReauth((visible, success) => {
        if (!visible && success) {
          fetchMe();
        }
      }),
    []
  );

  useEffect(() => {
    if (!applicationError) {
      return;
    }
    if (applicationError === CANCEL_REQUEST_MESSAGE) {
      appNotification.warning({
        description: applicationError,
        key: applicationError,
        duration: 5,
        message: 'Warning',
        onClose: resetErrorMessage,
        placement: 'bottomRight',
      });
    } else {
      appNotification.error({
        description: applicationError,
        key: applicationError,
        duration: 5,
        message: 'Error',
        onClose: resetErrorMessage,
        placement: 'bottomRight',
      });
    }
  });

  // разрешаем tenantId из пути после загрузки tenants (на старте UPDATE_LOCATION видит пустой список).
  useEffect(() => {
    if (!tenants.length) {
      return;
    }
    const fromPath = getTenantFromPath(pathname);
    if (tenants.includes(fromPath)) {
      setTenantId(fromPath);
    } else if (tenants.length === 1) {
      setTenantId(tenants[0]);
    }
  }, [tenants]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorText: 'rgba(0,0,0,.65)',
          fontSize: 16,
          lineHeight: 1.5715,
        },
      }}
    >
      <Provider store={store}>
        <NotificationBridge />
        <ReauthModal />
        <StyleSheetManager disableVendorPrefixes={process.env.NODE_ENV === 'development'}>
          <Routes>
            <Route
              path={LOCATION.ERROR}
              element={
                <PrivateRoute>
                  <Error />
                </PrivateRoute>
              }
            />
            <Route
              path="/:tenantId/*"
              element={
                <PrivateRoute>
                  <Main />
                </PrivateRoute>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Blank />
                </PrivateRoute>
              }
            />
            <Route
              path={LOCATION.UNKNOWN}
              element={
                <PrivateRoute>
                  <Unknown />
                </PrivateRoute>
              }
            />
          </Routes>
        </StyleSheetManager>
      </Provider>
    </ConfigProvider>
  );
};

export default Root;
